// src/oura/components/docs/boardModel.ts
//
// The staff documents board, as pure helpers: which group a doc sits in, the
// order the groups read in, what is late, a doc's name in the decided format
// and what its name says about its codes. Formatting and sorting
// only: every state and every count comes from the server.

import { useEffect, useState } from "react";
import { joinCodes, type BusinessDocuments, type CodeRow, type DocFlagCode, type FileType, type StaffDocument } from "@service/aeo/clientDocs";
import { expectedDocFor, isMonthlyType, nameMonth } from "@service/aeo/clientDocsRules";
import { DOC_STATUS, type DrawerStatus, type StatusStyle, type Tone } from "@oura/ui";
import { DUE_BY_NOTE, byOldestWait, codeDueText, codeLabel, docCodeChips, docDate, docLateText, docTypeTitle, isCodeOverdue, plural, replyByText, staffDocTitle, waitingDaysText } from "@oura/docs";
import { namePreview, outletPlace, planMonthOn } from "./create/createModel";

/* ------------------------------------------------------------------------- *
 * Groups: who moves next
 * ------------------------------------------------------------------------- */

/**
 * The board's groups. Late docs sit in Waiting on client with their late
 * text; "Comments not read" holds a file whose comments Oura has not read
 * (a PDF, a Sheet, or a doc before its first read); "Internal files" are the
 * ones staff marked "Not a client doc"; "Converted to Google Docs" holds the
 * Word files already converted, since the Google Doc is the one worked on (#4588).
 */
export type BoardGroupKey =
    | "needs_us"
    | "waiting_on_client"
    | "not_sent_yet"
    | "approved"
    | "done"
    | "not_shared"
    | "nothing_asked"
    | "not_read"
    | "no_code"
    | "converted"
    | "internal";

export interface BoardGroupMeta {
    key: BoardGroupKey;
    style: StatusStyle;
    /** Rows shown before "Show N more"; null shows them all. */
    shown: number | null;
    /** Someone waits on its rows, us or the client. With none of these holding a row, nothing waits on either side. */
    waits: boolean;
    /** Open until someone folds it: the groups someone acts on. The rest start folded. */
    open: boolean;
}

/**
 * In the order they read: the groups someone acts on first, open, then the
 * quiet ones, folded. No code yet is ours to act on (Pick a code), so it
 * sits with them.
 */
export const BOARD_GROUPS: BoardGroupMeta[] = [
    { key: "needs_us", style: DOC_STATUS.needs_us.staff, shown: null, waits: true, open: true },
    { key: "waiting_on_client", style: DOC_STATUS.waiting_on_client.staff, shown: null, waits: true, open: true },
    { key: "not_sent_yet", style: DOC_STATUS.not_sent_yet.staff, shown: null, waits: true, open: true },
    { key: "no_code", style: DOC_STATUS.no_code.staff, shown: 3, waits: false, open: true },
    { key: "approved", style: DOC_STATUS.approved.staff, shown: 3, waits: false, open: false },
    { key: "done", style: DOC_STATUS.done.staff, shown: 3, waits: false, open: false },
    { key: "not_shared", style: DOC_STATUS.not_shared.staff, shown: 3, waits: false, open: false },
    { key: "nothing_asked", style: DOC_STATUS.nothing_asked.staff, shown: 3, waits: false, open: false },
    { key: "not_read", style: { label: "Comments not read", tone: "gray", muted: true }, shown: 3, waits: false, open: false },
    { key: "converted", style: { label: "Converted to Google Docs", tone: "gray", muted: true }, shown: 3, waits: false, open: false },
    { key: "internal", style: { label: "Internal files", tone: "gray", muted: true }, shown: 3, waits: false, open: false },
];

export const BOARD_GROUP: Record<BoardGroupKey, BoardGroupMeta> = Object.fromEntries(BOARD_GROUPS.map((g) => [g.key, g])) as Record<BoardGroupKey, BoardGroupMeta>;

/** The group a doc sits in. */
export function groupOfDoc(doc: Pick<StaffDocument, "notClientDoc" | "codes" | "state" | "fileType" | "convertedTo">): Exclude<BoardGroupKey, "not_sent_yet"> {
    if (doc.notClientDoc) return "internal";
    // Whatever its state: the Google Doc it became carries the work now.
    if (doc.fileType === "word" && doc.convertedTo) return "converted";
    if (doc.codes.length === 0) return "no_code";
    switch (doc.state) {
        case "needs_us":
            return "needs_us";
        case "late":
        case "waiting_on_client":
            return "waiting_on_client";
        case "approved":
            return "approved";
        case "done":
            return "done";
        case "not_shared":
            return "not_shared";
        case "nothing_asked":
            return "nothing_asked";
        default:
            return "not_read";
    }
}

/** When a doc that exists but is not shared was due: "Was due Mon 12 Oct", late in rose. */
export interface DocDue {
    text: string;
    late: boolean;
}

/**
 * Whether the docs a code already has make up all the plan expects of it:
 * they only wait to be shared. A code that wants more docs than exist (1 pack
 * per outlet, 1 per plan month) is not covered: the missing ones still need
 * creating.
 */
export function isCoveredByDocs(r: Pick<CodeRow, "documentIds" | "expectedDocs">): boolean {
    return r.documentIds.length >= Math.max(1, r.expectedDocs ?? 1);
}

export interface BoardGroups {
    docs: Record<Exclude<BoardGroupKey, "not_sent_yet">, StaffDocument[]>;
    /** Codes the plan expects a doc for by now, with no doc made (or too few). */
    notSent: CodeRow[];
    /** The same codes as the docs that clear them: 1 row per doc to send. */
    toSend: DocToSend[];
    /** By doc id: when a not-shared doc was due, taken from the codes it covers. */
    dueByDoc: Map<string, DocDue>;
}

/** What naming a doc to send needs from a code row: route 7's CodeRow, or All clients' trimmed NotSentCode. */
export type ToSendInput = Pick<CodeRow, "code" | "outlet" | "expectedBy" | "state" | "late"> & { expectedMonth?: number | null };

/**
 * A doc the plan expects by now and nobody has shared, with the codes it
 * clears: "Answer Plan (S1, S3, B1)". The catalogue says which doc covers a
 * code (DOC_EXPECTED); a code no doc covers is its own row.
 */
export interface DocToSend<R extends ToSendInput = CodeRow> {
    key: string;
    /** "Answer Plan", "Google profile pack"; null for a code no catalogue doc covers. */
    docType: string | null;
    /** The code rows it clears, in the board's order. */
    rows: R[];
    /** The earliest due date among them (YYYY-MM-DD): the doc is due by the first. */
    expectedBy: string | null;
    expectedMonth: number | null;
    /** Any of its codes is past its due date. */
    late: boolean;
}

/**
 * The codes not sent yet, as the docs that clear them. Codes 1 doc covers
 * share a row; S2 is 1 pack per outlet, so each outlet is its own row. The
 * rows keep the order of their first code; a doc is due by its earliest
 * code, and late once any of its codes is.
 */
export function docsToSend<R extends ToSendInput>(rows: readonly R[]): Array<DocToSend<R>> {
    const out = new Map<string, DocToSend<R>>();
    for (const r of rows) {
        const doc = expectedDocFor(r.code);
        const key = doc ? `${doc.docType}|${doc.perOutlet ? r.outlet ?? "" : ""}` : `code|${r.code}|${r.outlet ?? ""}`;
        const at: DocToSend<R> = out.get(key) ?? { key, docType: doc?.docType ?? null, rows: [], expectedBy: null, expectedMonth: null, late: false };
        at.rows.push(r);
        if (r.expectedBy && (!at.expectedBy || r.expectedBy < at.expectedBy)) {
            at.expectedBy = r.expectedBy;
            at.expectedMonth = r.expectedMonth ?? null;
        }
        if (isCodeOverdue(r)) at.late = true;
        out.set(key, at);
    }
    return Array.from(out.values());
}

/** A doc to send by name and codes: "Answer Plan (S1, S3, B1)", "Google profile pack (S2-MK)". */
export function docToSendName(d: Pick<DocToSend<ToSendInput>, "docType" | "rows">): string {
    const labels = joinCodes(d.rows.map((r) => codeLabel(r.code, r.outlet)));
    return d.docType ? `${d.docType} (${labels})` : labels;
}

/** Code rows not sent yet and past their due date: the codes that count as late (YW, 29 Sep 2026). */
export function lateCodeRows(board: Pick<BusinessDocuments, "codes">): CodeRow[] {
    return board.codes.filter(isCodeOverdue);
}

const byName = (a: StaffDocument, b: StaffDocument) => a.name.localeCompare(b.name);
const byLastPost = (a: StaffDocument, b: StaffDocument) => (b.lastPost?.at ?? "").localeCompare(a.lastPost?.at ?? "");

/** Every doc in its group, oldest wait first where someone is waiting. */
/** A doc whose own run is `running` before the rest. */
function byWritingFirst(a: StaffDocument, b: StaffDocument): number {
    return Number(b.skillRun?.status === "running") - Number(a.skillRun?.status === "running");
}

export function groupBoard(board: Pick<BusinessDocuments, "documents" | "codes">): BoardGroups {
    const docs: BoardGroups["docs"] = {
        needs_us: [],
        waiting_on_client: [],
        approved: [],
        done: [],
        not_shared: [],
        nothing_asked: [],
        not_read: [],
        no_code: [],
        converted: [],
        internal: [],
    };
    for (const d of board.documents) docs[groupOfDoc(d)].push(d);
    docs.needs_us.sort(byOldestWait);
    docs.waiting_on_client.sort(byOldestWait);
    docs.approved.sort(byLastPost);
    docs.not_shared.sort(byName);
    docs.nothing_asked.sort(byName);
    docs.not_read.sort(byName);
    docs.no_code.sort(byName);
    docs.converted.sort(byName);
    docs.internal.sort(byName);
    // A doc a skill is still writing leads its group, so "Show N more" never hides
    // its "Oura is writing the draft…" (#4391). Stable: the rest keep their order.
    for (const key of Object.keys(docs) as (keyof typeof docs)[]) docs[key].sort(byWritingFirst);
    // A code the plan expects a doc for, whose docs exist and are only not
    // shared, is not "not sent yet": that doc is on the board as Not shared,
    // and listing the plan's expectation beside it made one job read as two
    // (with a Create that would have made a second doc). Its due date moves to
    // the doc's own row.
    const notSentAll = board.codes.filter((r) => r.state === "not_sent_yet");
    const notSent = notSentAll.filter((r) => !isCoveredByDocs(r));
    const dueByDoc = new Map<string, DocDue>();
    for (const d of docsToSend(notSentAll.filter(isCoveredByDocs))) {
        const text = codeDueText({ expectedBy: d.expectedBy, expectedMonth: d.expectedMonth, state: "not_sent_yet", late: d.late });
        if (!text) continue;
        for (const r of d.rows) {
            for (const id of r.documentIds) {
                const at = dueByDoc.get(id);
                if (!at || (d.late && !at.late)) dueByDoc.set(id, { text, late: d.late });
            }
        }
    }
    return { docs, notSent, toSend: docsToSend(notSent), dueByDoc };
}

/** How many rows a group holds: docs, or for Not sent yet the docs to send. */
export function groupCount(g: BoardGroups, key: BoardGroupKey): number {
    return key === "not_sent_yet" ? g.toSend.length : g.docs[key].length;
}

/* ------------------------------------------------------------------------- *
 * Words
 * ------------------------------------------------------------------------- */

/** "a PDF", "a Sheet": for "Oura doesn't read comments in a PDF". */
export const FILE_NOUN: Record<FileType, string> = {
    gdoc: "a Google Doc",
    word: "a Word file",
    pdf: "a PDF",
    sheet: "a Sheet",
    other: "this kind of file",
};

/** FILE_NOUN for a file type the server sends that this build does not know: "this kind of file". */
export function fileNoun(t: string): string {
    return FILE_NOUN[t as FileType] ?? FILE_NOUN.other;
}

/** The kit's 44px tap target, re-exported for the board's controls. */
export { TAP_TARGET } from "@oura/ui";

/**
 * Why a file has no code, in the server's words: "No code in the name", or
 * the bracket group in its name Oura could not read as a code.
 */
export function noCodeText(doc: Pick<StaffDocument, "flags">): string {
    return doc.flags.find((f) => f.code === "no_code")?.message ?? "No code in the name";
}

const LINK_VERB: Record<"reader" | "commenter" | "writer", string> = { reader: "view", commenter: "comment", writer: "edit" };

/**
 * The row tag for a flag whose code this build has no style for (the server
 * adds codes, and a failed fill's flag is one). Its `message` is a whole
 * sentence, which a tag cannot hold: it covered the row's title and chips.
 * The drawer's Fix first carries the sentence; the row says only that
 * something needs a look.
 */
export const UNKNOWN_FLAG_LABEL = "Needs attention";

/** A red flag's tag on a row: the fact, short. "Anyone with the link can edit", "2 internal notes visible". */
export function flagTagLabel(doc: Pick<StaffDocument, "sharing" | "notCounted">, code: DocFlagCode, fallback: string): string {
    const verb = doc.sharing.linkSharing ? LINK_VERB[doc.sharing.linkSharing] : undefined;
    if (code === "link_sharing" && verb) return `Anyone with the link can ${verb}`;
    if (code === "internal_notes" && doc.notCounted && doc.notCounted.internalNotes > 0) {
        const n = doc.notCounted.internalNotes;
        return `${plural(n, "internal note")} visible`;
    }
    return fallback;
}

/** The amber flags a row may tag, in this order. The drawer's File block says each in full. */
const ROW_AMBER = ["name_title_disagree", "code_not_in_contract", "comments_unreadable", "resolved_without_agreement"] as const;

/**
 * The 1 amber flag a row tags, if any; a folded group's head counts the rows
 * that have one. Comments not read already says so in its group's head.
 */
export function rowAmberFlag(doc: Pick<StaffDocument, "flags">, group: BoardGroupKey) {
    return ROW_AMBER.filter((code) => code !== "comments_unreadable" || group !== "not_read")
        .map((code) => doc.flags.find((f) => f.code === code && f.tone === "amber"))
        .find((f) => f !== undefined);
}

/**
 * A flag's message as a finished sentence, for when a fix follows it: the
 * server may send a fragment ("2 internal notes the client can read").
 */
export function asSentence(message: string): string {
    const m = message.trim();
    return /[.!?]$/.test(m) ? m : `${m}.`;
}

/**
 * The short fix that follows a flag's fact, in the drawer's "Fix first".
 * The link-sharing message carries its own; No code yet has its own button.
 */
export const FLAG_FIX: Partial<Record<DocFlagCode, string>> = {
    edit_access: "Make them a commenter in Drive's share settings.",
    internal_notes: "Delete them in the doc.",
    name_title_disagree: "Oura files it under the code in the name.",
    word_file: "Convert it to a Google Doc, then share the new copy with the client.",
    code_not_in_contract: "Change the code, or check the contract before the client replies.",
    // Oura's account is named by route 1, not here, and this map has no read
    // of it — so it says which account without naming the wrong one (#3974).
    comments_unreadable: "Read the folder again. If it fails again, check Oura's Google account can still open the file.",
    resolved_without_agreement: "Ask the client to confirm in the doc, or reopen the question.",
};

/**
 * What is late, docs and codes in one clause: "2 docs are late.", "1 code is
 * late.", "2 docs and 1 code are late." Null when nothing is. All clients'
 * sentence; the Client tab's also counts questions (pageSentence.tsx).
 */
export function lateText(docs: number, codes: number): string | null {
    const parts = [docs > 0 && plural(docs, "doc"), codes > 0 && plural(codes, "code")].filter((p): p is string => !!p);
    if (parts.length === 0) return null;
    return `${parts.join(" and ")} ${docs + codes === 1 ? "is" : "are"} late.`;
}

/* ------------------------------------------------------------------------- *
 * Names
 * ------------------------------------------------------------------------- */

/** Today in Malaysian time, "YYYY-MM-DD". */
export function myToday(now: number = Date.now()): string {
    return new Date(now + 8 * 3_600_000).toISOString().slice(0, 10);
}

/** "1" to "1.0"; a doc's version always has its minor part. */
function houseVersion(v: string | null): string {
    if (!v) return "1.0";
    return v.includes(".") ? v : `${v}.0`;
}

export interface DecidedName {
    /** `(<codes>) <Client>_<Doc>_<Location>_<DD MMMM YYYY>`, codes as "S2-S4"; `_<ver>` last only on a doc made before #4874. */
    name: string;
    /** Line 1 of the doc as Create writes it: "Answer Plan (S1, S3, B1, G4)", "GBP Pack, Mont Kiara (S2-MK)". */
    title: string;
    /** Whether the file's own name already reads this way. */
    nameMatches: boolean;
    /** Whether line 1 already reads this way; null for a file with no page title to read. */
    titleMatches: boolean | null;
}

/** The doc's name and page title in the decided format, or null for a doc with no code. */
export function decidedName(doc: StaffDocument, businessName: string, now: number = Date.now(), plan: BusinessDocuments["plan"] = null): DecidedName | null {
    const labels = docCodeChips(doc).map((c) => c.label);
    if (labels.length === 0) return null;
    const type = docTypeTitle(doc);
    // The server's doc date, and today when it has none or it does not read as
    // a day: a name with "NaN/undefined/NaN" in it would be worse than today's.
    const day = doc.docDate && /^\d{4}-\d{2}-\d{2}$/.test(doc.docDate) ? doc.docDate : myToday(now);
    // Line 1 by Create's own rule, so the 2 screens cannot drift: a doc for one outlet keeps the outlet name its
    // line 1 carries (F14), unless the type read from an older name already says it ("GBP Pack Mont Kiara").
    const place = outletPlace({ documents: [doc] }, doc.outlet);
    const kept = place && !type.toLowerCase().includes(place.toLowerCase()) ? place : null;
    // The file name comes from the same builder as Create's and the server's,
    // so the preview, Rename and what the backend writes cannot drift apart.
    // A doc made before #4874 keeps its own version in its name; a newer one has none.
    // A monthly doc keeps the plan month its name gives, else takes its day's, as the server's rename does
    // (ourteam-backend#6868).
    const month = nameMonth(doc.name) ?? (isMonthlyType(type) ? planMonthOn({ plan }, day) : null);
    const { name, title } = namePreview(businessName, type, doc.codes, doc.outlet, day, doc.version ? houseVersion(doc.version) : null, kept, month);
    const bare = doc.name.replace(/\.(docx?|pdf|xlsx?)$/i, "").trim();
    const line1 = doc.pageTitle?.split("\n")[0]?.trim() ?? null;
    return { name, title, nameMatches: bare === name, titleMatches: line1 === null ? null : line1 === title };
}

export interface NameTag {
    text: string;
    tone: "gray" | "blue" | "amber";
}

/** What the file's name says about its codes: "S1, S3 in the name", "No code in the name. Picked by hand: S1." */
export function nameTag(doc: StaffDocument): NameTag {
    const labels = joinCodes(docCodeChips(doc).map((c) => c.label));
    if (doc.notClientDoc) return { text: "Marked internal. Not a client doc.", tone: "gray" };
    if (!labels) return { text: noCodeText(doc), tone: "gray" };
    const disagree = doc.flags.find((f) => f.code === "name_title_disagree");
    if (doc.codeSource === "name") return disagree ? { text: disagree.message, tone: "amber" } : { text: `${labels} in the name`, tone: "gray" };
    if (doc.codeSource === "title") return { text: `No code in the name. The page title has ${labels}.`, tone: "blue" };
    if (doc.codeSource === "staff") return { text: `No code in the name. Picked by hand: ${labels}.`, tone: "blue" };
    return { text: labels, tone: "gray" };
}

export const NAME_TAG_TEXT: Record<NameTag["tone"], string> = {
    gray: "text-gray-500",
    blue: "font-medium text-blue-700",
    amber: "font-medium text-amber-800",
};

/* ------------------------------------------------------------------------- *
 * The clock
 * ------------------------------------------------------------------------- */

/** The time now, ticking every `every` ms, so "Read 4 min ago" moves while the screen stays open. */
export function useNow(every = 30_000): number {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const t = window.setInterval(() => setNow(Date.now()), every);
        return () => window.clearInterval(t);
    }, [every]);
    return now;
}

/** A tone for a group head's dot. */
export const groupTone = (key: BoardGroupKey): Tone => BOARD_GROUP[key].style.tone;

/* ------------------------------------------------------------------------- *
 * The doc drawer's header (drawer specs v1.1, A-A and A-B)
 * ------------------------------------------------------------------------- */

/** The row's title: the doc type, or for a file with no code (or an internal one) its name, as on its row. */
export function docSheetTitle(doc: StaffDocument): string {
    return doc.codes.length === 0 || doc.notClientDoc ? doc.name : staffDocTitle(doc);
}

/** Pick a code's title. The same test as the doc's "Change the code" button. */
export function pickTitle(doc: Pick<StaffDocument, "codes" | "notClientDoc">): string {
    return doc.codes.length > 0 || doc.notClientDoc ? "Change the code" : "Pick a code";
}

/** A doc's first code not sent by its due date: "S4 was due Mon 14 Sep". */
function overdueCode(doc: StaffDocument, rows: readonly CodeRow[]): string | null {
    for (const chip of docCodeChips(doc)) {
        const row = rows.find((r) => r.code === chip.code && (r.outlet ?? null) === chip.outlet);
        const due = row ? docDate(row.expectedBy) : null;
        if (row && due && isCodeOverdue(row)) return `${chip.label} was due ${due}`;
    }
    return null;
}

/**
 * "● {group} · {clock}": the group the doc sits in and the mover's clock, in
 * working days (docs): how long it has waited on us, its reply-by or how
 * late (the date it was due on hover), when it was shared or edited.
 */
export function docStatusLine(doc: StaffDocument, group: BoardGroupKey, rows: readonly CodeRow[]): DrawerStatus {
    const style = BOARD_GROUP[group].style;
    const base = { tone: style.tone, label: style.label };
    const plain = (text: string | null | undefined) => (text ? { text } : null);
    const shared = docDate(doc.sharing.sharedAt);
    const edited = docDate(doc.modifiedAt);
    switch (group) {
        case "needs_us":
            return { ...base, clock: plain(waitingDaysText(doc.waitingWorkingDays)) };
        case "waiting_on_client": {
            const late = docLateText(doc);
            const due = docDate(doc.replyBy);
            // The date in words beside the count, not only in a tooltip that touch and keyboard never reach.
            if (late) return { ...base, clock: { text: due && (doc.lateWorkingDays ?? 0) > 0 ? `Was due ${due}, ${late}` : late, late: true } };
            return { ...base, clock: plain(replyByText(doc.replyBy) ?? waitingDaysText(doc.waitingWorkingDays)) };
        }
        case "not_shared": {
            const overdue = overdueCode(doc, rows);
            if (overdue) return { ...base, clock: { text: overdue, late: true, title: DUE_BY_NOTE } };
            return { ...base, clock: plain(edited && `Edited ${edited}`) };
        }
        case "no_code":
            return { ...base, clock: { text: doc.sharing.clientCanSee ? (shared ? `Shared ${shared}` : "Shared with the client") : "Not shared with the client" } };
        case "internal":
            return { ...base, clock: { text: "Oura never counts it" } };
        case "done":
            return { ...base, clock: plain(doc.done ? `Done ${docDate(doc.done.at) ?? ""} by ${doc.done.by}`.replace("  ", " ") : null) };
        default:
            return { ...base, clock: plain(shared ? `Shared ${shared}` : edited && `Edited ${edited}`) };
    }
}
